/**
 * Who was at the table, in a sentence (WI-043).
 *
 * Pure and framework-free, so it can be tested without a renderer.
 *
 * A player is not a user (ADR-0006): most of the people in these lists never
 * signed up and never will, so all there is to show is a name somebody typed.
 */

interface Attendee {
  displayName: string;
  isWinner?: boolean | null;
}

/**
 * "Dave", "Dave and Sam", "Dave, Sam and Ali", "Dave, Sam, Ali and 2 others".
 *
 * Truncates by count rather than by characters, for the same reason
 * `formatDesigners` does: a name cut in half reads like bad data rather than
 * like a deliberate summary.
 */
function joinWithAnd(names: string[]): string {
  if (names.length <= 1) return names.join("");
  // Sliced rather than indexed: under `noUncheckedIndexedAccess` every
  // `names[i]` is `string | undefined`, and asserting it away would be
  // pretending to know something the compiler is right to doubt.
  return `${names.slice(0, -1).join(", ")} and ${names.slice(-1).join("")}`;
}

export function formatAttendance(participants: Attendee[], limit = 3): string {
  const names = participants.map((p) => p.displayName);

  if (names.length === 0) return "";
  if (names.length <= limit) return joinWithAnd(names);

  const rest = names.length - limit;
  return `${names.slice(0, limit).join(", ")} and ${rest} other${rest === 1 ? "" : "s"}`;
}

/**
 * The winner's name, if anybody won.
 *
 * Separate from the attendance line because plenty of games do not have one,
 * and a co-op night should not be made to look like it does.
 */
export function winnerOf(participants: Attendee[]): string | null {
  return participants.find((p) => p.isWinner === true)?.displayName ?? null;
}

/**
 * A stable description of an attendance list, for telling one apart from
 * another.
 *
 * Used to decide whether saving a row needs to write participants at all.
 * Sorted, because the order the API returns is by winner then name, and the
 * order the UI builds is whatever order the chips were tapped in -- comparing
 * those directly would report a change every time.
 */
export function attendanceKey(participants: { playerId: string; isWinner?: boolean | null }[]): string {
  return participants
    .map((p) => `${p.playerId}:${p.isWinner === true ? "w" : ""}`)
    .sort()
    .join("|");
}
